import React from 'react';
import { Link } from 'react-router';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import { AssetUrls } from 'config/assets';
import { BlogAuthor } from 'types/interfaces/blog';
import './AuthorByline.css';

interface AuthorBylineProps {
  /** Omitted for articles with no author: the bar shows the TradeMagic logo and name, unlinked. */
  author?: BlogAuthor | null;
  publishedDate?: string | null;
  /** From utils/readTime; rendered as "N min read" after the date. */
  readTimeMinutes?: number;
  /** Rendered at the right end of the bar (the Day / Night switch). */
  actions?: React.ReactNode;
  /** Shown in place of the date when there is none (the admin preview says "Not yet published"). */
  unpublishedLabel?: string;
}

const AVATAR_SIZE = 40;

export const formatBylineDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

/**
 * One hairline bar under the hero: avatar, name, "date · read time", and the
 * reading-mode switch pushed right (Asana 1219952231021031, Decisions 8 and 9).
 * Used only by DynamicArticle and the admin ArticlePreviewPage.
 */
const AuthorByline: React.FC<AuthorBylineProps> = ({
  author,
  publishedDate,
  readTimeMinutes,
  actions,
  unpublishedLabel,
}) => {
  const dateText = publishedDate ? formatBylineDate(publishedDate) : unpublishedLabel;
  const meta = [dateText, readTimeMinutes ? `${readTimeMinutes} min read` : null].filter(Boolean).join(' · ');

  return (
    <div className="author-byline">
      {author ? (
        <AvatarWithFlag avatarUrl={author.avatarUrl} username={author.username} size={AVATAR_SIZE} alt="" />
      ) : (
        <img
          className="author-byline-logo"
          src={AssetUrls.logos.iconLogo}
          width={AVATAR_SIZE}
          height={AVATAR_SIZE}
          alt=""
          aria-hidden="true"
        />
      )}
      <div className="author-byline-text">
        {author ? (
          <Link to={`/profile/u/${encodeURIComponent(author.username)}`} className="author-byline-link">
            <span className="author-byline-name">{author.username}</span>
          </Link>
        ) : (
          <span className="author-byline-name">TradeMagic</span>
        )}
        {meta && <span className="author-byline-meta">{meta}</span>}
      </div>
      {actions && <div className="author-byline-actions">{actions}</div>}
    </div>
  );
};

export default AuthorByline;
